<template>
  <div ref="componentEl">
    <aw-section-wrapper :has-padding="false">
      <template #content>
        <client-only>
          <aw-tab-carousel
            ref="tabCarousel"
            :main-title="null"
            :empty-placeholder="$awt('aw.cms_carousel.fallback.text')"
            :tabs="tabs"
            :breakpoints="breakpoints"
            :slides="slides"
            tab-key="id"
            slide-key="id"
            :class="$style.tabCarousel"
            @render-sub-slides="onRenderSubSlides"
          >
            <template #slide(all)(all)="{ slide }">
              <div
                :data-banner-id="slide.id"
                :data-banner-index="getGlobalBannerIndex(slide)"
              >
                <aw-cms-card
                  :content="{...slide, backgroundColor: {hex: $styleVariables.colorBackground3, alpha: 1 }}"
                  :analytics-banner-interaction="extendAnalyticsBannerInteraction(slide)"
                  :analytics-measurable="analyticsMeasurable"
                  :full-component-link="slide.link"
                  :send-view-interaction="false"
                  bordered
                />
              </div>
            </template>
          </aw-tab-carousel>
        </client-only>
      </template>
    </aw-section-wrapper>
  </div>
</template>

<script setup>
  import { defineAsyncComponent, computed, ref, nextTick, toRef } from 'vue';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import analyticsBannerInteractionMixin from '~~/common/mixins/analyticsBannerInteractionMixin.js';
  import { useBannerInteractionAnalytics } from '~~/common/composables/useBannerInteractionAnalytics';

  const AwTabCarousel = defineAsyncComponent(() => import('~~/common/components/AwTabCarousel'));
  const AwSectionWrapper = defineAsyncComponent(() => import('~~/common/components/Page/Common/AwSectionWrapper'));
  const AwCmsCard = defineAsyncComponent(() => import('~~/common/components/AwStandardPartials/AwCmsCard'));

  const props = defineProps({
    ...analyticsBannerInteractionMixin.props,
    content: {
      type: Object,
      required: true,
    },
  });

  const componentEl = ref(null);
  const tabCarousel = ref(null);

  const userInterfaceStore = useUserInterfaceStore();
  const screenRange = computed(() => userInterfaceStore.mediaQueries);

  const bannersRef = computed(() => props.content.components);

  const {
    getGlobalBannerIndex,
    extendAnalyticsBannerInteraction,
    observeBannerCards,
  } = useBannerInteractionAnalytics({
    componentEl,
    banners: bannersRef,
    analyticsBannerInteraction: toRef(props, 'analyticsBannerInteraction'),
    analyticsMeasurable: toRef(props, 'analyticsMeasurable'),
  });

  function onRenderSubSlides () {
    nextTick(() => {
      nextTick(() => {
        observeBannerCards();
      });
    });
  }

  const tabs = [
    {
      id: 'current',
      tabTitle: '',
    },
  ];

  const slides = computed(() => ({
    current: props.content.components,
  }));

  const breakpoints = computed(() => [
    {
      at: screenRange.value['desktop-large-min'],
      count: 5,
    },
    {
      at: screenRange.value['desktop-medium-min'],
      count: 4,
    },
    {
      at: screenRange.value['tablet-min'],
      count: 3,
    },
    {
      at: true,
      count: 'auto',
    },
  ]);
</script>


<style module lang="scss" rel="stylesheet/scss">

.tabCarousel {
  --awTabItemPadding: 14px 16px;
  --awTabCarouselParentPaddingTop: 0;

  @include tablet(min) {
    --awTabItemPadding: 12px 16px;
    --awTabCarouselMinHeight: 100%;
  }
}

</style>
